import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { timeFromY, timeFromYKeepingMinutes, shiftAnchorByDays, hourlySlotBox } from "../../src/organize/task_calendar_move"
import { eventPopoverPosition } from "../../src/calendar/popover_position"

// Ptaszek zamknietego zadania na liscie dnia (odpowiednik helpera task_calendar_closed_icon w ERB).
const CLOSED_CHECK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" class=\"shrink-0 inline h-3.5 w-3.5 text-gray-900\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\"><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"3\" d=\"M5 13l4 4L19 7\" /></svg>"

// Szerokość panelu z detalami zadania (klasa w-72 w _calendar_event_popover.html.erb).
const POPOVER_WIDTH = 288
// Ruch w px, po którym naciśnięcie kafelka zamienia się z kliknięcia w przeciąganie.
const DRAG_THRESHOLD = 5
// Podgląd przeciągania zawsze przy samej górze komórki dnia (żeby był jednoznaczny).
const PREVIEW_TOP_INSET = 2
// Górny wiersz siatki godzinowej (grid-template-rows zaczyna się od 1.75rem) - obszar 24 h
// zaczyna się dopiero pod nim. Kolumna-cel obejmuje pełną wysokość razem z tym nagłówkiem,
// więc bez tego offsetu godzina/ramka rozjeżdżają się o ~jeden slot względem renderu eventu.
const HOUR_GRID_HEADER_REM = 1.75
// Event godzinowy renderuje się o 2 px niżej niż krawędź wiersza siatki (li ma mt-px,
// przycisk w środku top: 1px). Bez tego zamrożony kafelek po zapisie "opadał" o te 2 px.
const HOUR_EVENT_INSET_PX = 2
// Bezpiecznik: gdyby odświeżenie widoku po zapisie nie doszło (np. utrata sieci przy Turbo.visit),
// zamrożony kafelek zostałby na ekranie na zawsze - po tym czasie wracamy do stanu z DOM.
const FREEZE_FALLBACK_MS = 8000
// Okno po zakończeniu przeciągania, w którym akcje kliknięcia (nowe zadanie, popovery) są
// ignorowane. Blokada zdarzenia click nie wystarcza - jego target zależy od tego, co leży
// pod kursorem po upuszczeniu, więc czasem trafiał w tło dnia i otwierał formularz.
const CLICK_AFTER_DRAG_MS = 400

// Kontroler kalendarza zadań (Organize::Task). Uproszczona wersja calendar-dashboard:
// - brak modala "szybkiego tworzenia" - kliknięcie pustego dnia robi redirect na /organize/tasks/new
//   z prefillem deadline w query (?deadline=YYYY-MM-DD lub YYYY-MM-DDTHH:MM dla widoku tygodniowego).
// - kliknięcie zadania pokazuje popover z detalami (status, responsible, klient, recurring).
// - obsługuje też przycisk "więcej zadań" na dniu (popover z pełną listą) i widok mobilny.
// - drag&drop: przeciągnięcie kafelka zadania na inny dzień/godzinę zapisuje nową datę
//   (POST na data-move-url z nową kotwicą). Recurringi (read_only) nie są przeciągalne.
export default class extends Controller {
  static targets = [
    "dayEventsPopover", "dayEventsPanel", "dayEventsTitle", "dayEventsList",
    "eventDetail", "eventDetailPanel", "eventDetailName", "eventDetailSid", "eventDetailColor", "eventDetailClosed",
    "eventDetailTime", "eventDetailStatusRow", "eventDetailStatus",
    "eventDetailResponsibleRow", "eventDetailResponsible",
    "eventDetailRecurrenceRow", "eventDetailRecurrence",
    "eventDetailClientRow", "eventDetailClient",
    "eventDetailProjectRow", "eventDetailProject",
    "eventDetailTeamRow", "eventDetailTeam",
    "eventDetailPriority",
    "eventDetailEstimatedRow", "eventDetailEstimated",
    "eventDetailTagsRow", "eventDetailTags",
    "eventDetailDesc", "eventDetailShowLink", "eventDetailEditLink", "eventDetailCloseTask",
  ]

  static values = {
    newTaskUrl: String,
    eventsData: Object,
    selectedDate: String,
    moveErrorText: String, // komunikat gdy przeniesienie się nie udało i serwer nie podał powodu
    closeErrorText: String, // komunikat gdy zamknięcie zadania się nie udało i serwer nie podał powodu
  }

  connect() {
    // Przesuwalne kafelki nie mogą przewijać strony przy przeciąganiu dotykiem
    // (touch-action:none). Ustawiamy z JS, żeby nie zależeć od klasy Tailwind.
    this.element.querySelectorAll("[data-move-url]").forEach(el => {
      el.style.touchAction = "none"
    })
  }

  disconnect() {
    this._unfreeze()
  }

  // Kliknięcie pustego dnia/komórki/kolumny - redirect do /organize/tasks/new z prefillem terminu.
  // Dla widoku tygodniowego liczy też godzinę na podstawie Y kliknięcia w kolumnie.
  openNewTask(event) {
    if (this._afterDrag()) return // klik jest echem przeciągania, nie prośbą o nowe zadanie
    // Nie reaguj jeśli kliknięto na child (np. button zadania) - tylko bezpośredni klik w tło dnia.
    if (event.target !== event.currentTarget) return

    const date = event.currentTarget.dataset.date
    if (!date) return

    let deadline = date
    const rect = event.currentTarget.getBoundingClientRect()
    // jeśli kolumna jest wysoka (>200px) - traktuj jako siatka godzinowa i policz godzinę
    if (rect.height > 200) {
      deadline = `${date}T${this._timeFromColumnY(rect, event.clientY)}`
    }

    const url = new URL(this.newTaskUrlValue, window.location.origin)
    url.searchParams.set("deadline", deadline)
    window.location.href = url.toString()
  }

  // --- Przeciąganie zadań (własna implementacja na pointer events) ---
  // Natywny HTML5 DnD podnosi tylko jeden złapany węzeł, a zadanie wielodniowe jest pocięte
  // na osobne kafelki dnia. Dlatego robimy własny drag: pływająca pigułka jedzie za kursorem
  // jako CAŁE zadanie, a cel (dzień/godzinę) wykrywamy przez elementFromPoint. Działa też na dotyku.

  // Naciśnięcie kafelka zadania - zapamiętuje dane, ale nie startuje jeszcze przeciągania
  // (dopiero ruch powyżej progu; poniżej progu to zwykły klik -> popover).
  pointerDown(event) {
    if (event.button > 0) return // pomiń środkowy/prawy przycisk myszy
    // drugi palec / mysz w trakcie trwającego przeciągania: bez tego nadpisalibyśmy this._onMove
    // i poprzedni listener zostałby na window na zawsze (pointerUp zdejmuje tylko aktualną referencję)
    if (this._press || !event.isPrimary) return
    if (this._frozen) return // czekamy na odświeżenie widoku po poprzednim przeniesieniu
    const el = event.currentTarget
    if (!el.dataset.moveUrl) return // nieprzesuwalne (recurring)

    this._press = {
      el,
      moveUrl: el.dataset.moveUrl,
      anchor: el.dataset.anchor,
      sourceDate: el.dataset.segDate,
      allDay: el.dataset.allDay === "true",
      // strefa startu: kafelek godzinowy jest w siatce (bez przodka data-calendar-cell),
      // całodniowy/dzienny jest w komórce dnia. Cel dropu musi być z tej samej strefy -
      // godzinowe i całodniowe to w tygodniu dwa niezależne pola przeciągania.
      originHourly: !el.closest("[data-calendar-cell]"),
      startX: event.clientX,
      startY: event.clientY,
    }
    this._dragging = false

    this._onMove = this._pointerMove.bind(this)
    this._onUp = this._pointerUp.bind(this)
    window.addEventListener("pointermove", this._onMove)
    window.addEventListener("pointerup", this._onUp)
    window.addEventListener("pointercancel", this._onUp)
  }

  _pointerMove(event) {
    if (!this._press) return

    if (!this._dragging) {
      const dist = Math.hypot(event.clientX - this._press.startX, event.clientY - this._press.startY)
      if (dist < DRAG_THRESHOLD) return
      this._startDragging(event)
    }

    event.preventDefault()
    const cell = this._targetCellAt(event.clientX, event.clientY)
    this._positionFloat(event.clientX, event.clientY, cell)
    this._setHighlight(cell)
  }

  _pointerUp(event) {
    window.removeEventListener("pointermove", this._onMove)
    window.removeEventListener("pointerup", this._onUp)
    window.removeEventListener("pointercancel", this._onUp)

    if (this._dragging) {
      // pointercancel nie ma sensownych współrzędnych - traktuj jak anulowanie (brak zapisu)
      const cell = event.type === "pointercancel" ? null : this._targetCellAt(event.clientX, event.clientY)
      if (cell) {
        // podgląd zostaje tam, gdzie upuszczono (zamrożony), aż widok się odświeży
        this._positionFloat(event.clientX, event.clientY, cell)
        this._freezeDragAt(cell, event.clientY)
        this._dropOnCell(cell, event.clientY)
      } else {
        this._finishDragging()
      }
      this._dragEndedAt = performance.now()
      this._suppressNextClick() // po przeciągnięciu nie otwieraj popovera
    }
    this._press = null
    this._dragging = false
  }

  // Czy kliknięcie jest echem właśnie zakończonego przeciągania (albo trwa zamrożenie przed
  // odświeżeniem widoku). Wołane na wejściu akcji click, żeby nie reagować na taki klik.
  _afterDrag() {
    if (this._frozen) return true
    return this._dragEndedAt != null && performance.now() - this._dragEndedAt < CLICK_AFTER_DRAG_MS
  }

  // Start właściwego przeciągania: przyciemnij segmenty zadania, zbierz uporządkowane komórki
  // dni (do układania klona wg siatki) i zbuduj klony segmentów.
  _startDragging(event) {
    this._dragging = true
    this._lastStart = null
    document.body.style.userSelect = "none"
    this._dragSegments = Array.from(this.element.querySelectorAll("[data-move-url]"))
      .filter(seg => seg.dataset.moveUrl === this._press.moveUrl)
    this._dragSegments.forEach(seg => seg.classList.add("opacity-40"))
    // widoczne komórki dni w kolejności DOM (miesiąc: 42 komórki, tydzień all-day: 7).
    // Selektor data-calendar-cell (nie data-date!) - żeby do listy nie trafiły inne
    // elementy z data-date (np. przycisk "+N więcej"), co psułoby mapowanie indeks->dzień.
    // Kolumny godzinowe (wysokie) pomijamy - tam podgląd podąża za kursorem.
    this._dayCells = Array.from(this.element.querySelectorAll("[data-calendar-cell]"))
      .filter(c => c.offsetParent !== null && c.getBoundingClientRect().height < 200)
    this._targetCells = null // odśwież geometryczną listę celów dla tego przeciągania
    this._scrollContainer = undefined // cache kontenera scrolla/sticky - liczony raz przy pierwszym _hourlyClip
    // offset złapanego segmentu w pasku (0 = pierwszy dzień)
    this._grabOffset = Math.max(0, this._dragSegments.indexOf(this._press.el))
    // pozycja kafelka w pionie wewnątrz komórki dnia z momentu chwytu - podgląd trzyma tę
    // wysokość zamiast podskakiwać pod górną krawędź komórki
    this._grabCellOffsetY = this._cellOffsetY(this._press.el)
    // boczne marginesy kafelka w komórce - podgląd nie może rozciągać się na całą szerokość
    // komórki, bo po zapisie kafelek wracał do węższego renderu i "ściskał się"
    this._segInsets = this._dragSegments.map(seg => this._cellInsetsX(seg))
    this._createFloat(this._dragSegments, event.clientX, event.clientY)
  }

  _finishDragging() {
    document.body.style.userSelect = ""
    if (this._dragSegments) {
      this._dragSegments.forEach(seg => seg.classList.remove("opacity-40"))
      this._dragSegments = null
    }
    this._clearHighlight()
    if (this._segClones) {
      this._segClones.forEach(clone => clone.remove())
      this._segClones = null
    }
    if (this._dropIndicator) {
      this._dropIndicator.remove()
      this._dropIndicator = null
    }
    this._targetCells = null
    this._scrollContainer = undefined
  }

  // Zakończenie przeciągania z zapisem: NIE sprzątamy podglądu. Klony zostają w miejscu
  // upuszczenia (bez cienia, pełna widoczność - wyglądają jak zwykły kafelek), a oryginalne
  // segmenty chowamy. Inaczej między pointerup i re-renderem widoku zadanie na moment
  // "wracałoby" na starą pozycję i migało. Ostateczny układ liczy dalej serwer.
  _freezeDragAt(cell, clientY) {
    document.body.style.userSelect = ""
    this._clearHighlight()
    // w siatce godzinowej klon jedzie za kursorem - przystaw go do slotu, w który wpadnie
    if (!this._press.allDay && cell.getBoundingClientRect().height > 200) this._snapFloatToSlot(cell, clientY)
    if (this._dropIndicator) {
      this._dropIndicator.remove()
      this._dropIndicator = null
    }

    this._frozen = { clones: this._segClones || [], segments: this._dragSegments || [] }
    this._frozen.clones.forEach(clone => {
      clone.style.opacity = "1"
      clone.style.boxShadow = ""
    })
    // opacity 0 (nie visibility/display) - kafelek znika, ale zostaje w układzie komórki
    // ORAZ w hit-teście: klik generowany po przeciągnięciu musi trafić w niego, a nie w tło
    // dnia pod nim (to odpalałoby openNewTask i przenosiło na formularz nowego zadania).
    this._frozen.segments.forEach(seg => {
      seg.classList.remove("opacity-40")
      seg.style.opacity = "0"
    })
    this._frozenFallback = setTimeout(() => this._unfreeze(), FREEZE_FALLBACK_MS)

    this._segClones = null
    this._dragSegments = null
    this._targetCells = null
    this._scrollContainer = undefined
  }

  // Zdjęcie zamrożenia - podgląd znika, kafelki wracają do stanu z DOM. Wołane gdy zapis
  // się nie udał, przy odświeżeniu widoku (disconnect / turbo:before-cache) i z bezpiecznika.
  _unfreeze() {
    if (!this._frozen) return
    clearTimeout(this._frozenFallback)
    this._frozenFallback = null
    this._frozen.clones.forEach(clone => clone.remove())
    this._frozen.segments.forEach(seg => {
      seg.style.opacity = ""
    })
    this._frozen = null
  }

  // Klon = jeden element na każdy dzień paska. Nie translujemy sztywnej bryły - każdy dzień
  // układamy osobno w docelowej komórce siatki (patrz _placeSegmentsAt), dzięki czemu przy
  // końcu tygodnia "ogonek" naturalnie przechodzi do nowej linii.
  _createFloat(segments, grabX, grabY) {
    // W siatce godzinowej podgląd ma od razu pokazywać miejsce, w które zadanie wyląduje, czyli
    // całą kolumnę dnia - a nie ułamek szerokości, jaki kafelek dostał z powodu nakładania się
    // z innymi zadaniami (o tej samej godzinie mogło ich być kilka). Wymiary bierzemy więc z <li>
    // (komórka kolumny), a nie z kafelka. W miesiącu <li> nie ma i zostaje prostokąt kafelka.
    const rects = segments.map(seg => (seg.closest("li") || seg).getBoundingClientRect())
    this._segClones = segments.map((seg, i) => {
      const r = rects[i]
      const clone = seg.cloneNode(true)
      clone.classList.remove("opacity-40")
      clone.style.position = "fixed"
      clone.style.margin = "0"
      clone.style.zIndex = "9999"
      clone.style.pointerEvents = "none"
      clone.setAttribute("data-turbo-temporary", "") // Turbo usuwa przed zapisem snapshotu
      clone.style.opacity = "0.9"
      clone.style.boxShadow = "0 8px 16px rgba(0,0,0,.35)"
      clone.style.height = `${r.height}px`
      clone.style.width = `${r.width}px`
      clone.style.left = `${r.left}px`
      clone.style.top = `${r.top}px`
      document.body.appendChild(clone)
      return clone
    })
    // chwyt do trybu kursora (siatka godzinowa / pojedynczy segment)
    const gr = rects[this._grabOffset] || rects[0]
    this._grabDX = gr ? grabX - gr.left : 0
    this._grabDY = gr ? grabY - gr.top : 0
    // wysokość eventu (px) = długość w siatce godzinowej - do ramki slotu (gdzie wpadnie)
    this._eventHeight = rects[0] ? rects[0].height : 0
  }

  // Pozycjonowanie podglądu:
  // - siatka godzinowa (tydzień) / brak siatki dni: pojedynczy klon podąża za kursorem,
  // - w przeciwnym razie: każdy dzień paska ląduje w kolejnej komórce dnia (z zawijaniem
  //   między tygodniami). Gdy kursor trafi w szczelinę - trzymamy ostatni start (bez bobowania).
  _positionFloat(x, y, cell) {
    if (!this._segClones) return

    const hourly = !this._press.allDay && cell && cell.getBoundingClientRect().height > 200
    if (hourly) {
      // klon jedzie za kursorem (trzymany element), a niebieska ramka pokazuje slot docelowy
      this._followCursor(x, y)
      this._placeHourlyIndicator(cell, y)
      return
    }
    this._hideHourlyIndicator()
    if (this._dayCells.length === 0) {
      this._followCursor(x, y)
      return
    }

    if (cell) {
      const idx = this._dayCells.indexOf(cell)
      if (idx >= 0) this._lastStart = idx - this._grabOffset
    }
    if (this._lastStart == null) {
      this._followCursor(x, y)
      return
    }
    this._placeSegmentsAt(this._lastStart)
  }

  // Odległość górnej krawędzi kafelka od góry jego komórki dnia (px). Trzymana przy
  // przeciąganiu, żeby zadanie nie "podnosiło się" w komórce po złapaniu.
  _cellOffsetY(el) {
    const cell = el.closest("[data-calendar-cell]")
    if (!cell) return PREVIEW_TOP_INSET
    const offset = el.getBoundingClientRect().top - cell.getBoundingClientRect().top
    return Math.max(offset, PREVIEW_TOP_INSET)
  }

  // Boczne marginesy kafelka wewnątrz jego komórki dnia (px).
  _cellInsetsX(el) {
    const cell = el.closest("[data-calendar-cell]")
    if (!cell) return { left: 0, right: 0 }
    const segRect = el.getBoundingClientRect()
    const cellRect = cell.getBoundingClientRect()
    return {
      left: Math.max(segRect.left - cellRect.left, 0),
      right: Math.max(cellRect.right - segRect.right, 0),
    }
  }

  // Układa segmenty w kolejnych komórkach dni od indeksu start; segmenty poza siatką chowa.
  // Pozycja w komórce (pion i boczne marginesy) = ta z momentu chwytu, żeby podgląd wyglądał
  // dokładnie jak kafelek po zapisie; pion przycięty, by nie wystawał poniżej komórki.
  _placeSegmentsAt(start) {
    this._segClones.forEach((clone, i) => {
      const target = this._dayCells[start + i]
      if (!target) {
        clone.style.display = "none"
        return
      }
      const r = target.getBoundingClientRect()
      const maxOffset = Math.max(r.height - (this._eventHeight || 0) - PREVIEW_TOP_INSET, PREVIEW_TOP_INSET)
      const offset = Math.min(this._grabCellOffsetY ?? PREVIEW_TOP_INSET, maxOffset)
      const inset = (this._segInsets && this._segInsets[i]) || { left: 0, right: 0 }
      clone.style.display = ""
      clone.style.left = `${r.left + inset.left}px`
      clone.style.top = `${r.top + offset}px`
      clone.style.width = `${Math.max(r.width - inset.left - inset.right, 1)}px`
    })
  }

  // Tryb kursora (siatka godzinowa / brak celu) - pojedynczy segment przy kursorze.
  _followCursor(x, y) {
    const first = this._segClones[0]
    if (!first) return
    first.style.display = ""
    first.style.left = `${x - this._grabDX}px`
    first.style.top = `${y - this._grabDY}px`
  }

  // Wysokość górnego wiersza siatki godzinowej (1.75rem) w px - offset obszaru 24 h.
  // Memoizowane: getComputedStyle jest kosztowny, a przy przeciąganiu w siatce godzin
  // wołalibyśmy je dwa razy na każdym pointermove.
  _hourGridHeaderPx() {
    if (!this._hourGridHeader) {
      const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      this._hourGridHeader = HOUR_GRID_HEADER_REM * rootFont
    }
    return this._hourGridHeader
  }

  // "HH:MM" (snap 30 min) z pozycji kursora w kolumnie godzinowej, z uwzględnieniem górnego
  // nagłówka siatki - dzięki temu godzina zgadza się z liniami godzinowymi i renderem eventu.
  // Z podanym `anchor` (przeciąganie zadania) snapuje przesunięcie, zachowując końcówkę
  // minutową zadania; bez niego (klik w pustą komórkę) snapuje do pełnej pół godziny.
  _timeFromColumnY(rect, clientY, anchor = null) {
    const header = this._hourGridHeaderPx()
    const usable = Math.max(rect.height - header, 1)
    const y = Math.max(0, clientY - rect.top - header)
    return anchor ? timeFromYKeepingMinutes(y, usable, anchor) : timeFromY(y, usable)
  }

  // Widoczny obszar siatki godzinowej (kontener overflow-auto minus przyklejony nagłówek).
  // Wysoka kolumna sięga rect-em daleko poza ekran (za sticky header), a ramka jest position:fixed
  // i nie jest clipowana scrollem - bez tego rysowała się "duchem" na białym tle nad siatką.
  // Kontener i sticky-header wyszukujemy raz na przeciągnięcie (getComputedStyle jest kosztowny -
  // nie wolno go wołać na każdym pointermove); w trakcie liczymy tylko świeży getBoundingClientRect.
  _hourlyClip(cell) {
    if (this._scrollContainer === undefined) {
      let container = cell.parentElement
      while (container) {
        const oy = getComputedStyle(container).overflowY
        if (oy === "auto" || oy === "scroll") break
        container = container.parentElement
      }
      this._scrollContainer = container || null
      this._stickyHeader = container ? container.querySelector(".sticky") : null
    }
    if (!this._scrollContainer) return null
    const cr = this._scrollContainer.getBoundingClientRect()
    const top = this._stickyHeader ? this._stickyHeader.getBoundingClientRect().bottom : cr.top
    return { top, bottom: cr.bottom }
  }

  // Górna krawędź eventu (kursor - offset chwytu) przycięta do widocznego obszaru siatki.
  // Wspólna dla ramki i zapisu - żeby to co widać zgadzało się z tym co się zapisze.
  _clampedEventTopY(cell, clientY) {
    let y = clientY - this._grabDY
    const clip = this._hourlyClip(cell)
    if (clip) y = Math.min(Math.max(y, clip.top), clip.bottom)
    return y
  }

  // Ramka slotu w siatce godzinowej (analogicznie do obrysu komórki dnia w miesiącu) - pokazuje,
  // w którą godzinę wpadnie event. Pozycja i wysokość liczone jak przy renderze eventu (nagłówek
  // 1.75rem + obszar 24 h pod nim), więc ramka pokrywa się z miejscem, gdzie zadanie wyląduje.
  // Rysowana tylko w widocznym obszarze siatki (przycięta do kontenera scrolla).
  _placeHourlyIndicator(cell, clientY) {
    const box = this._hourlySlotRect(cell, clientY)
    if (!box) {
      this._hideHourlyIndicator()
      return
    }

    const el = this._ensureHourlyIndicator()
    el.style.display = ""
    el.style.left = `${box.left}px`
    el.style.top = `${box.top}px`
    el.style.width = `${box.width}px`
    el.style.height = `${box.height}px`
  }

  // Prostokąt slotu (px, współrzędne ekranu), w który wpadnie przeciągane zadanie.
  // null gdy slot wypada poza widocznym obszarem siatki (przycięcie do kontenera scrolla).
  _hourlySlotRect(cell, clientY) {
    const rect = cell.getBoundingClientRect()
    const [hh, mm] = this._timeFromColumnY(rect, this._clampedEventTopY(cell, clientY), this._press.anchor).split(":").map(Number)
    const box = hourlySlotBox(hh * 60 + mm, rect.top + HOUR_EVENT_INSET_PX, rect.height, this._hourGridHeaderPx(), this._eventHeight, this._hourlyClip(cell))
    if (!box) return null
    return { left: rect.left, width: rect.width, top: box.top, height: box.height }
  }

  // Przystawia klon do ramki slotu (siatka godzinowa) - używane przed zamrożeniem, żeby
  // upuszczone zadanie stało tam, gdzie pokazywała ramka, nie przy kursorze.
  _snapFloatToSlot(cell, clientY) {
    const clone = this._segClones && this._segClones[0]
    const box = this._hourlySlotRect(cell, clientY)
    if (!clone || !box) return

    clone.style.left = `${box.left}px`
    clone.style.top = `${box.top}px`
    clone.style.width = `${box.width}px`
    clone.style.height = `${box.height}px`
  }

  _ensureHourlyIndicator() {
    if (!this._dropIndicator) {
      const el = document.createElement("div")
      el.style.position = "fixed"
      el.style.pointerEvents = "none"
      el.style.zIndex = "9998" // pod klonem (9999), nad treścią
      el.style.border = "2px solid rgb(96, 165, 250)" // blue-400
      el.style.borderRadius = "8px"
      el.style.background = "rgba(96, 165, 250, .15)"
      document.body.appendChild(el)
      this._dropIndicator = el
    }
    return this._dropIndicator
  }

  _hideHourlyIndicator() {
    if (this._dropIndicator) this._dropIndicator.style.display = "none"
  }

  // Komórka-cel (dzień/kolumna) pod punktem - wyszukiwana GEOMETRYCZNIE, nie przez elementFromPoint.
  // Kolumny godzinowe to nakładka-siostra kafelków zadań (nie ich rodzic), więc closest() nad
  // zajętym slotem nie znalazłby kolumny - stąd ramka skakała i nie dało się upuścić na inne zadanie.
  // Kandydaci: komórki dni/all-day (data-calendar-cell) oraz wysokie kolumny godzinowe (data-date).
  // Przy nakładających się trafieniach wybieramy mniejszą (all-day nad kolumną godzinową).
  _targetCellAt(x, y) {
    if (!this._targetCells) {
      const originHourly = this._press && this._press.originHourly
      this._targetCells = Array.from(this.element.querySelectorAll("[data-date]"))
        .filter(c => {
          if (c.offsetParent === null) return false // niewidoczne (np. layout mobilny na desktopie)
          const tall = c.getBoundingClientRect().height > 200
          // strefa godzinowa = tylko wysokie kolumny; strefa dzienna/all-day = tylko komórki dni.
          // Dzięki temu nie da się przeciągnąć całodniowego w siatkę godzin ani odwrotnie.
          return originHourly ? tall : c.hasAttribute("data-calendar-cell")
        })
    }
    let best = null
    this._targetCells.forEach(c => {
      const r = c.getBoundingClientRect()
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        if (!best || r.height < best.height) best = { el: c, height: r.height }
      }
    })
    return best ? best.el : null
  }

  _setHighlight(cell) {
    // w siatce godzinowej nie obrysowujemy całej kolumny - tam slot pokazuje ramka (_placeHourlyIndicator)
    if (cell && cell.getBoundingClientRect().height > 200) cell = null
    if (cell === this._highlighted) return
    this._clearHighlight()
    if (cell) {
      cell.classList.add("ring-2", "ring-inset", "ring-blue-400")
      this._highlighted = cell
    }
  }

  _clearHighlight() {
    if (this._highlighted) {
      this._highlighted.classList.remove("ring-2", "ring-inset", "ring-blue-400")
      this._highlighted = null
    }
  }

  // Liczy nową kotwicę na podstawie komórki celu i zapisuje.
  // - całodniowe: zawsze zmiana dnia (bez godziny), także w tygodniu.
  // - godzinowe w siatce tygodnia (kolumna >200px): dzień + godzina z pozycji Y.
  // - poza tym (miesiąc): przesunięcie o dni z zachowaniem godziny.
  _dropOnCell(cell, clientY) {
    const dropDate = cell.dataset.date
    if (!dropDate || !this._press) return

    const rect = cell.getBoundingClientRect()
    let newAnchor
    if (!this._press.allDay && rect.height > 200) {
      // godzina liczona od górnej krawędzi eventu, przyciętej do widocznego obszaru - spójnie z ramką.
      // anchor przekazany, żeby zachować końcówkę minutową zadania (10:07 -> 14:07, nie 14:00)
      newAnchor = `${dropDate}T${this._timeFromColumnY(rect, this._clampedEventTopY(cell, clientY), this._press.anchor)}`
    } else {
      newAnchor = shiftAnchorByDays(this._press.anchor, this._press.sourceDate, dropDate)
    }
    this._submitMove(this._press.moveUrl, newAnchor)
  }

  // Po przeciągnięciu pointerup generuje click - przechwytujemy go raz i blokujemy, żeby nie
  // otworzył popovera ani (gdy trafi w tło dnia) formularza nowego zadania. Blokujemy KAŻDY
  // pierwszy klik, bo jego target zależy od tego, co leży pod kursorem po upuszczeniu.
  // Okno jest krótkie i zdejmowane po pierwszym kliku, więc kolejne kliki działają normalnie.
  _suppressNextClick() {
    const cleanup = () => window.removeEventListener("click", handler, true)
    const handler = (e) => {
      e.stopPropagation()
      e.preventDefault()
      cleanup()
    }
    window.addEventListener("click", handler, true)
    setTimeout(cleanup, 250)
  }

  // POST nowej kotwicy; po sukcesie odświeża widok przez Turbo Drive (spójnie z nawigacją
  // kalendarza - płynny re-render bez twardego przeładunku; układ dalej liczony server-side).
  _submitMove(url, target) {
    const csrfToken = document.querySelector("[name=\"csrf-token\"]").content
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify({ target }),
    })
      .then(response => {
        if (response.ok) {
          // Turbo na ten sam URL najpierw renderuje podgląd ze snapshotu (loadCachedSnapshot),
          // czyli STARY układ - zadanie błyskało z powrotem na poprzedniej pozycji na czas
          // pobrania strony. Czyścimy cache (brak podglądu) i wyłączamy zapis tej strony do
          // snapshotu (zamrożony podgląd nie ma tam czego szukać).
          Turbo.cache.exemptPageFromCache()
          Turbo.cache.clear()
          return Turbo.visit(window.location.href, { action: "replace" })
        }

        // powód niepowodzenia przychodzi w body ({ errors: [...] }) - bez tego kafelek
        // wracałby na miejsce bez żadnego wyjaśnienia
        // nieudany zapis - zdejmij zamrożony podgląd, kafelek wraca na starą pozycję
        this._unfreeze()
        return response.json()
          .catch(() => ({}))
          .then(data => this._notifyMoveError((data.errors || []).join(", ")))
      })
      .catch(error => {
        console.error("Move error:", error)
        this._unfreeze()
        this._notifyMoveError()
      })
  }

  // Komunikat o nieudanym przeniesieniu (flash z IntumJs). Treść z serwera wstawiamy jako
  // tekst - notify robi innerHTML, a errors mogą zawierać dane wpisane przez użytkownika.
  _notifyMoveError(message) {
    const text = message || this.moveErrorTextValue
    const holder = document.createElement("div")
    holder.textContent = text
    window.IntumJs?.notify(holder.innerHTML, "error")
  }

  // Mobile: zaznacz dzień (analogicznie do calendar-dashboard).
  selectDay(event) {
    const button = event.currentTarget
    this.element.querySelectorAll("button[data-is-selected]").forEach(btn => {
      btn.removeAttribute("data-is-selected")
    })
    button.setAttribute("data-is-selected", "")
    this.selectedDateValue = button.dataset.date
  }

  // Popover z pełną listą zadań danego dnia (po kliknięciu "+N więcej").
  showDayEvents(event) {
    event.stopPropagation()
    if (this._afterDrag()) return
    const date = event.currentTarget.dataset.date
    const dayData = this.eventsDataValue[date] || {}
    const events = dayData.events || []
    // Na mobile ten sam handler wisi na przycisku dnia - pusty dzień nie otwiera popovera.
    if (events.length === 0) return

    this.dayEventsTitleTarget.textContent = dayData.title || date

    const items = events.map(ev => {
      const evData = JSON.stringify(ev)
      const priorityHtml = this._priorityIconHtml(ev.priority)
      const opacityClass = ev.read_only ? "opacity-70" : ""
      const closedIcon = ev.closed ? CLOSED_CHECK_SVG : ""
      return `<li>
        <button type="button" data-event='${this._escapeAttr(evData)}' data-action="click->task-calendar#showEventDetail" class="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 w-full text-left ${opacityClass}">
          <span class="size-2 shrink-0 rounded-full" style="background-color: ${this._escapeHtml(ev.color)};"></span>
          <span class="text-xs text-gray-500">${this._escapeHtml(ev.time)}</span>
          ${priorityHtml ? `<span class="shrink-0">${priorityHtml}</span>` : ""}
          ${closedIcon}
          <span data-event-title class="flex-auto truncate text-sm font-medium text-gray-900 group-hover:text-blue-600">${this._escapeHtml(ev.name)}</span>
        </button>
      </li>`
    }).join("")
    this.dayEventsListTarget.innerHTML = items

    const btn = event.currentTarget
    const rect = btn.getBoundingClientRect()
    const panel = this.dayEventsPanelTarget
    this.dayEventsPopoverTarget.classList.remove("hidden")

    const panelWidth = panel.offsetWidth
    let left = rect.left + rect.width / 2 - panelWidth / 2
    let top = rect.bottom + 4
    if (left < 8) left = 8
    if (left + panelWidth > window.innerWidth - 8) left = window.innerWidth - panelWidth - 8
    if (top + panel.offsetHeight > window.innerHeight - 8) top = rect.top - panel.offsetHeight - 4
    if (top < 8) top = 8
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  closeDayEvents() {
    this.dayEventsPopoverTarget.classList.add("hidden")
    this._setActiveEvent(null)
  }

  // Popover z detalami pojedynczego zadania.
  showEventDetail(event) {
    event.stopPropagation()
    if (this._afterDrag()) return
    if (!this.hasEventDetailTarget) return

    // Podświetl klikniety element listy dnia, by bylo widac ktore zadanie ma otwarte szczegoly.
    this._setActiveEvent(event.currentTarget)

    const data = JSON.parse(event.currentTarget.dataset.event)

    this.eventDetailNameTarget.textContent = data.name
    if (this.hasEventDetailClosedTarget) this.eventDetailClosedTarget.classList.toggle("hidden", !data.closed)
    this.eventDetailColorTarget.style.backgroundColor = data.color || "#3b82f6"
    this.eventDetailTimeTarget.textContent = data.time

    if (data.sid && this.hasEventDetailSidTarget) {
      this.eventDetailSidTarget.textContent = data.sid
      this.eventDetailSidTarget.classList.remove("hidden")
    } else if (this.hasEventDetailSidTarget) {
      this.eventDetailSidTarget.classList.add("hidden")
    }

    // show_url może być nullem dla wirtualnych recurringów - wtedy ukrywamy link
    if (data.show_url) {
      this.eventDetailShowLinkTarget.href = data.show_url
      this.eventDetailShowLinkTarget.classList.remove("hidden")
    } else {
      this.eventDetailShowLinkTarget.classList.add("hidden")
    }
    if (data.edit_url) {
      this.eventDetailEditLinkTarget.href = data.edit_url
      this.eventDetailEditLinkTarget.classList.remove("hidden")
    } else {
      this.eventDetailEditLinkTarget.classList.add("hidden")
    }

    // Przycisk "Zamknij zadanie" - tylko dla otwartych, nierecurringowych (close_url niepuste).
    this._closeTaskUrl = data.close_url || null
    if (this.hasEventDetailCloseTaskTarget) {
      this.eventDetailCloseTaskTarget.classList.toggle("hidden", !this._closeTaskUrl)
    }

    this._toggleRow("Status", data.status_name)
    this._toggleRow("Responsible", data.responsible_name)
    this._toggleRow("Recurrence", data.recurrence)
    this._togglePriority(data.priority)
    this._toggleRow("Project", data.project_name)
    this._toggleRow("Team", data.team_name)
    this._toggleRow("Estimated", data.estimated_hours ? `${data.estimated_hours} h` : null)
    this._toggleTags(data.tag_names)

    if (data.client_name && this.hasEventDetailClientRowTarget) {
      this.eventDetailClientTarget.textContent = data.client_name
      if (data.client_url) this.eventDetailClientTarget.href = data.client_url
      this.eventDetailClientRowTarget.classList.remove("hidden")
    } else if (this.hasEventDetailClientRowTarget) {
      this.eventDetailClientRowTarget.classList.add("hidden")
    }

    if (data.description) {
      this.eventDetailDescTarget.textContent = data.description
      this.eventDetailDescTarget.classList.remove("hidden")
    } else {
      this.eventDetailDescTarget.classList.add("hidden")
    }

    this.eventDetailTarget.classList.remove("hidden")

    const rect = event.currentTarget.getBoundingClientRect()
    const panel = this.eventDetailPanelTarget
    const box = eventPopoverPosition(
      rect,
      { width: POPOVER_WIDTH, height: panel.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight },
    )

    panel.style.left = `${box.left}px`
    panel.style.top = `${box.top}px`
  }

  closeEventDetail() {
    if (this.hasEventDetailTarget) {
      this.eventDetailTarget.classList.add("hidden")
    }
    this._setActiveEvent(null)
  }

  // Zamyka zadanie (POST /close) prosto z podgladu, potem odswieza kalendarz (jak przy move).
  closeTask() {
    if (!this._closeTaskUrl) return
    const csrfToken = document.querySelector("[name=\"csrf-token\"]").content
    fetch(this._closeTaskUrl, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
    })
      .then(response => {
        if (response.ok) {
          Turbo.cache.exemptPageFromCache()
          Turbo.cache.clear()
          return Turbo.visit(window.location.href, { action: "replace" })
        }
        // Akcja close zwraca surowe errors zadania ({ closed: ["Masz niezamkniete podzadania"] }),
        // nie { errors: [...] } - splaszczamy wartosci hasha do listy komunikatow.
        return response.json().catch(() => ({})).then(data => {
          const messages = Array.isArray(data.errors) ? data.errors : Object.values(data || {}).flat()
          this._notifyCloseError(messages.filter(Boolean).join(", "))
        })
      })
      .catch(error => {
        console.error("Close error:", error)
        this._notifyCloseError()
      })
  }

  _notifyCloseError(message) {
    const text = message || this.closeErrorTextValue
    const holder = document.createElement("div")
    holder.textContent = text
    window.IntumJs?.notify(holder.innerHTML, "error")
  }

  // Utrzymuje wyglad hovera na kliknietym zadaniu - na liscie dnia i w komorkach kalendarza -
  // zeby bylo widac ktore ma otwarte szczegoly. Czysci poprzednie zaznaczenie, potem zamraza
  // biezace. Button bez data-event (np. null) tylko czysci.
  _setActiveEvent(button) {
    this.element.querySelectorAll("[data-active-event]").forEach(b => this._freezeEventHover(b, false))
    if (button && button.hasAttribute("data-event")) this._freezeEventHover(button, true)
  }

  // Zamraza (on=true) lub zdejmuje te same klasy, ktore danemu przyciskowi daje :hover:
  // tlo (hover:bg-gray-50), przyciemnienie (hover:opacity-80) i niebieski tytul (group-hover:text-blue-600).
  _freezeEventHover(button, on) {
    button.toggleAttribute("data-active-event", on)
    if (button.classList.contains("hover:bg-gray-50")) button.classList.toggle("bg-gray-50", on)
    if (button.classList.contains("hover:opacity-80")) button.classList.toggle("opacity-80", on)
    const title = button.querySelector("[data-event-title]")
    if (title) {
      title.classList.toggle("text-blue-600", on)
      title.classList.toggle("text-gray-900", !on)
    }
  }

  // Pokazuje ikonę priorytetu obok tytułu (null = default normalny, ukryj).
  _togglePriority(value) {
    if (!this.hasEventDetailPriorityTarget) return
    const variants = this.eventDetailPriorityTarget.querySelectorAll("[data-priority-value]")
    variants.forEach(el => el.classList.add("hidden"))
    if (value === null || value === undefined) {
      this.eventDetailPriorityTarget.classList.add("hidden")
      return
    }
    const match = this.eventDetailPriorityTarget.querySelector(`[data-priority-value="${value}"]`)
    if (!match) {
      this.eventDetailPriorityTarget.classList.add("hidden")
      return
    }
    match.classList.remove("hidden")
    this.eventDetailPriorityTarget.classList.remove("hidden")
  }

  // Zwraca HTML ikony priorytetu (klonowany ze schowanego template'u w eventDetailPriorityTarget)
  // do osadzenia w innych miejscach (np. dayEvents popover).
  _priorityIconHtml(value) {
    if (value === null || value === undefined) return ""
    if (!this.hasEventDetailPriorityTarget) return ""
    const match = this.eventDetailPriorityTarget.querySelector(`[data-priority-value="${value}"]`)
    if (!match) return ""
    // innerHTML to <div class="relative h-X w-X"><svg .../></div> - zwracamy bez klasy "hidden"
    return match.innerHTML
  }

  // Renderuje tagi jako kolorowe pigułki (lub ukrywa wiersz, gdy brak).
  _toggleTags(tagNames) {
    if (!this.hasEventDetailTagsRowTarget || !this.hasEventDetailTagsTarget) return
    if (!tagNames || tagNames.length === 0) {
      this.eventDetailTagsRowTarget.classList.add("hidden")
      return
    }
    this.eventDetailTagsTarget.innerHTML = tagNames.map(name =>
      `<span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 mr-1">${this._escapeHtml(name)}</span>`,
    ).join("")
    this.eventDetailTagsRowTarget.classList.remove("hidden")
  }

  // Pokazuje/ukrywa wiersz popovera w zależności od dostępności wartości.
  _toggleRow(suffix, value) {
    const rowKey = `eventDetail${suffix}Row`
    const valKey = `eventDetail${suffix}`
    const hasRow = this[`has${rowKey.charAt(0).toUpperCase() + rowKey.slice(1)}Target`]
    const hasVal = this[`has${valKey.charAt(0).toUpperCase() + valKey.slice(1)}Target`]
    if (!hasRow || !hasVal) return
    const rowEl = this[`${rowKey}Target`]
    const valEl = this[`${valKey}Target`]
    if (value) {
      valEl.textContent = value
      rowEl.classList.remove("hidden")
    } else {
      rowEl.classList.add("hidden")
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    if (str == null) return ""
    return str.replace(/&/g, "&amp;").replace(/'/g, "&#39;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
